import { computed, makeObservable, observable } from 'mobx'
import { once } from 'lodash'
import { Partial } from 'react-spring'
import { MembershipDTO } from 'iam/dtos/workspace'
import { PrimeFullMember } from 'iam/MemberStore'
import { PrimeFullWorkspace } from 'iam/WorkspaceStore'
import { Membership } from 'iam/WorkspaceMembershipStore'
import { Model } from 'infra/model'
import { Id } from 'types/definitions'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { generateAvatarFallback } from 'utils/image-util'
import { match } from 'utils/pattern-match-util'
import { SearchableUserDTO } from '../dtos/searchable-user'
import Member from './Member'
import { UserDataType } from './UserDataType'
import Workspace from './Workspace'

export class SearchableUser extends Model<SearchableUserDTO, SearchableUser> {
  @observable userId = ''
  @observable publicId = Id.none
  @observable givenName = ''
  @observable familyName = ''
  @observable email = ''
  @observable phone = ''
  @observable picture = ''
  @observable userDataType = UserDataType.User
  @observable dateCreated = DateZero()
  @observable dateModified = DateZero()
  @observable lastSeen = DateZero()
  @observable online = false
  @observable memberships: Membership[] = []
  @observable isWorkspaceCustomer = false

  static get none() {
    return lazyNone()
  }

  constructor(
    private primeFullMember: PrimeFullMember,
    private primeFullWorkspace: PrimeFullWorkspace,
  ) {
    super()
    makeObservable(this)
  }

  @computed
  get avatar() {
    return (
      this.picture || generateAvatarFallback(this.displayName || this.email)
    )
  }

  @computed
  get displayName() {
    return `${this.givenName} ${this.familyName}`
  }

  parseMembershipDto(dto: MembershipDTO[]): Membership[] {
    return dto.map((mb) => {
      return {
        workspace: this.primeFullWorkspace(mb.workspace),
        member: this.primeFullMember(mb.member),
      }
    })
  }

  parse(userDTO: Partial<SearchableUserDTO>): Partial<SearchableUser> {
    return {
      userId: userDTO.user_id || '',
      publicId: userDTO.public_id,
      userDataType: userDTO.user_data_type
        ? match(userDTO.user_data_type, {
            USER: () => UserDataType.User,
            PRO: () => UserDataType.Pro,
            ADMIN: () => UserDataType.Admin,
            CALL_AGENT: () => UserDataType.CallAgent,
            [match.DEFAULT]: () => UserDataType.User,
          })
        : UserDataType.User,
      givenName: userDTO.given_name,
      familyName: userDTO.family_name,
      email: userDTO.email,
      phone: userDTO.phone || '',
      picture: userDTO.picture,
      lastSeen: maybeParseDate(userDTO.last_seen),
      dateCreated: maybeParseDate(userDTO.date_created),
      dateModified: maybeParseDate(userDTO.date_modified),
      memberships: userDTO.memberships
        ? this.parseMembershipDto(userDTO.memberships)
        : [],
      isWorkspaceCustomer: userDTO.is_workspace_customer ?? false,
    }
  }
}

const lazyNone = once(() =>
  new SearchableUser(
    () => Member.none,
    () => Workspace.none,
  ).set({
    givenName: '',
    familyName: '',
    email: '',
  }),
)
